import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { FetchingBar } from './FetchingBar';
import { StreakCountdown } from './StreakCountdown';
import { useMediaQuery, LG_UP } from '../../hooks/use-media-query';
import { useNotificationStream } from '../../hooks/use-notifications';

// Logged-in chrome. Two layouts from one tree:
//   lg and up — a permanent sidebar column + sticky topbar, pages in the main column.
//   below lg  — the sidebar becomes an off-canvas drawer over the content, opened from the
//               topbar's hamburger. A 260px column on a 375px phone left ~115px for the page,
//               which is what made every screen behind login unusable on mobile.
export function AppShell() {
  const [compact, setCompact] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery(LG_UP);

  // Hold the live notification stream open for the whole authenticated session. At shell level
  // rather than inside NotificationBell so navigating around the app doesn't tear the connection
  // down and rebuild it, and so a page that unmounts the topbar (none today) couldn't silently
  // kill delivery. Degrades to the bell's poll wherever streaming isn't available.
  useNotificationStream();

  // Tapping a nav row should land you on the page, not leave the drawer sitting over it. Keyed on
  // pathname rather than wired into the links so it also covers programmatic navigation.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  // Crossing up to the permanent-sidebar layout retires the drawer: leaving `navOpen` true would
  // keep the body scroll-locked (below) on a desktop viewport with no visible drawer to close.
  useEffect(() => {
    if (isDesktop) setNavOpen(false);
  }, [isDesktop]);

  // Escape to dismiss + body scroll lock while the drawer covers the page — same pattern as Modal,
  // so every full-screen overlay in the app behaves the same way.
  useEffect(() => {
    if (!navOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setNavOpen(false);
    }
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [navOpen]);

  return (
    <div
      className={
        // One column on mobile (the sidebar is taken out of flow and floated over it below); the
        // two-column grid only exists from lg up, so the transition is scoped there too — otherwise
        // it would animate the column swap on every resize across the breakpoint.
        'grid min-h-screen grid-cols-1 lg:transition-[grid-template-columns] lg:duration-200 ' +
        (compact ? 'lg:grid-cols-[64px_1fr]' : 'lg:grid-cols-[260px_1fr]')
      }
    >
      {/* Drawer backdrop — below lg only, and only while open. Click anywhere off the panel closes
          it, which is the gesture people try first. */}
      {navOpen && (
        <div
          className="fixed inset-0 z-40 bg-ink/60 backdrop-blur-sm lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <Sidebar
        // The collapse toggle is a desktop affordance: a drawer is already dismissible, and a
        // 64px icon rail inside an overlay is a worse target than the labelled rows. Forcing it
        // off below lg also stops a desktop-collapsed sidebar from opening as an icon-only drawer
        // after a resize.
        compact={isDesktop && compact}
        onToggleCompact={() => setCompact((c) => !c)}
        showCompactToggle={isDesktop}
        onClose={() => setNavOpen(false)}
        // Off-canvas below lg (slid out unless open), a normal sticky grid child from lg up.
        // `w-[260px]` is pinned only while floating; in the grid the column owns the width.
        className={
          'fixed inset-y-0 left-0 z-50 w-[260px] transition-transform duration-200 ease-out ' +
          'lg:sticky lg:top-0 lg:z-auto lg:w-auto lg:translate-x-0 lg:transition-none ' +
          (navOpen ? 'translate-x-0 shadow-2' : '-translate-x-full')
        }
      />

      <div className="flex min-w-0 flex-col overflow-x-hidden">
        <Topbar onOpenNav={() => setNavOpen(true)} />
        {/* Global stale-while-revalidate cue: pulses under the sticky topbar whenever any query
            is refetching, so every list gets the "updating…" sign for free. */}
        <FetchingBar />
        {/* Padding steps down on small screens — px-8 spent 64px of a 375px viewport on margins. */}
        <main className="mx-auto w-full max-w-[1920px] px-4 pb-20 pt-5 sm:px-6 sm:pt-6 lg:px-8 lg:pt-7">
          <Outlet />
        </main>
      </div>
      {/* Renders nothing unless a live streak is actually at risk today — see the component. It
          lives at shell level rather than on one page because the deadline applies wherever the
          referee happens to be in the app. */}
      <StreakCountdown />
    </div>
  );
}
